import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const featureTabs = [
  {
    key: "core", label: "Core", emoji: "⚡",
    features: [
      "Unlimited digital menus with easy online editor",
      "QR ordering for tables, rooms & counters",
      "Instant orders on WhatsApp + live dashboard",
      "Supports Dine-in, Delivery & Takeaway",
      "GST-ready bill generation & reports",
    ],
  },
  {
    key: "growth", label: "Growth", emoji: "🚀",
    features: [
      "Customer data capture for repeat marketing",
      "Send offers & promotions via WhatsApp",
      "Multi-language menu (all Indian languages)",
      "Sales analytics — top items, peak hours",
      "Free updates with 24×7 WhatsApp support",
    ],
  },
  {
    key: "restaurant", label: "Restaurants", emoji: "🍽️",
    features: [
      "Table-wise QR ordering for faster service",
      "Reduce order mistakes & staff dependency",
      "Higher table turnover during peak hours",
      "Instant WhatsApp alerts for kitchen & owner",
      "Perfect for dine-in, takeaway & delivery",
    ],
  },
  {
    key: "hotel", label: "Hotels", emoji: "🏨",
    features: [
      "Room-wise QR menus for in-room dining",
      "Sell spa, laundry & hotel services",
      "Reduce front desk & room-service calls",
      "Multi-language support for guests",
      "Works for rooms, lobby & poolside",
    ],
  },
  {
    key: "salon", label: "Salons & Spas", emoji: "💇",
    features: [
      "QR service menu for easy booking",
      "Sell treatments, packages & services",
      "Appointment booking via WhatsApp",
      "Share menu via WhatsApp & social media",
      "Perfect for salons, spas & wellness studios",
    ],
  },
];

const included = [
  "1-month free trial",
  "No credit card needed",
  "Setup in 5 minutes",
  "24×7 WhatsApp support",
];

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useWindowWidth() {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setTimeout(() => setVis(true), delay);
          obs.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, vis };
}

/* ─────────────────────────────────────────────
   TOKENS
───────────────────────────────────────────── */
const G = "#6cbb23";
const T = {
  zh: { fontFamily: "'Fraunces',Georgia,serif" },
  zb: { fontFamily: "'Plus Jakarta Sans',sans-serif" },
};

const revealStyle = (vis: boolean, delay = 0): React.CSSProperties => ({
  opacity: vis ? 1 : 0,
  transform: vis ? "translateY(0)" : "translateY(22px)",
  transition: `opacity .58s ${delay}ms cubic-bezier(.22,.68,0,1.2), transform .58s ${delay}ms cubic-bezier(.22,.68,0,1.2)`,
});

/* ─────────────────────────────────────────────
   FONT + MINIMAL STYLES
───────────────────────────────────────────── */
const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

    .pc-btn-primary {
      background: ${G}; color: #fff; border: none; cursor: pointer;
      transition: transform .18s, box-shadow .18s, background .18s;
      position: relative; overflow: hidden;
    }
    .pc-btn-primary::after {
      content: ''; position: absolute; inset: 0;
      background: rgba(255,255,255,.12);
      opacity: 0; transition: opacity .18s;
    }
    .pc-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(108,187,35,.38); }
    .pc-btn-primary:hover::after { opacity: 1; }
    .pc-btn-primary:active { transform: scale(.97); }

    .pc-btn-outline {
      background: transparent; cursor: pointer;
      transition: background .18s, color .18s, transform .18s;
    }
    .pc-btn-outline:hover { background: rgba(255,255,255,.1); transform: translateY(-2px); }
    .pc-btn-outline:active { transform: scale(.97); }

    .pc-tab {
      cursor: pointer; border: none;
      transition: background .18s, color .18s, border-color .18s, transform .15s;
    }
    .pc-tab:hover { transform: translateY(-1px); }

    .pc-feature-item {
      transition: transform .18s;
    }
    .pc-feature-item:hover { transform: translateX(3px); }

    .pc-pill { transition: border-color .18s; }
    .pc-pill:hover { border-color: ${G} !important; }

    @keyframes pc-check {
      from { transform: scale(0) rotate(-45deg); opacity: 0; }
      to   { transform: scale(1) rotate(0deg);   opacity: 1; }
    }
    .pc-check-anim { animation: pc-check .3s cubic-bezier(.22,.68,0,1.2) both; }

    @keyframes pc-fadein {
      from { opacity:0; transform: translateX(10px); }
      to   { opacity:1; transform: translateX(0); }
    }
    .pc-feature-enter { animation: pc-fadein .35s cubic-bezier(.22,.68,0,1.2) both; }
  `}</style>
);

/* ─────────────────────────────────────────────
   CHECK ICON
───────────────────────────────────────────── */
const Check = ({ color = G }: { color?: string }) => (
  <svg
    className="pc-check-anim"
    width="16" height="16" viewBox="0 0 16 16" fill="none"
    style={{ flexShrink: 0, marginTop: 2 }}
  >
    <circle cx="8" cy="8" r="7.25" fill={color === G ? "#f0f9e6" : "rgba(255,255,255,.15)"} />
    <path d="M5 8.2l2 2L11 5.5" stroke={color} strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const PricingCTA = () => {
  const navigate = useNavigate();
  const width      = useWindowWidth();
  const isMobile   = width < 768;
  const isDesktop  = width >= 960;

  const [activeTab, setActiveTab]   = useState("core");
  const [featureKey, setFeatureKey] = useState(0);

  const hdr  = useReveal(0);
  const card = useReveal(60);
  const bot  = useReveal(40);

  const handleTab = (key: string) => {
    setActiveTab(key);
    setFeatureKey(k => k + 1);
  };

  const activeFeatures = featureTabs.find(t => t.key === activeTab)?.features ?? [];
  const activeTab_ = featureTabs.find(t => t.key === activeTab)!;

  return (
    <section
      id="pricing"
      style={{
        background: "#fafaf8", position: "relative",
        overflow: "hidden", padding: "60px 0 30px",
        fontFamily: "'Plus Jakarta Sans',sans-serif",
      }}
    >
      <FontLink />

      {/* Grid texture */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(#efefea 1px,transparent 1px),linear-gradient(90deg,#efefea 1px,transparent 1px)",
        backgroundSize: "48px 48px", opacity: .5,
      }}/>

      {/* Glow */}
      <div style={{
        position: "absolute", top: -80, left: "50%", transform: "translateX(-50%)",
        width: 700, height: 400, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(108,187,35,.07) 0%,transparent 70%)",
        pointerEvents: "none",
      }}/>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>

        {/* ── HEADER ── */}
        <div ref={hdr.ref} style={{ ...revealStyle(hdr.vis), textAlign: "center", marginBottom: 48 }}>
        
          <h2 style={{
            ...T.zh, fontSize: "clamp(30px,3.8vw,50px)", fontWeight: 900,
            fontStyle: "italic", color: "#111", letterSpacing: "-0.03em",
            lineHeight: 1.27, marginBottom: 14,
          }}>
            One plan.{" "}
            <mark style={{ background: G, color: "#fff", padding: "1px 12px 5px", borderRadius: 6, fontStyle: "italic", display: "inline-block" }}>
              Everything included.
            </mark>
          </h2>
          <p style={{ ...T.zb, fontSize: 15, color: "#888", fontWeight: 300, maxWidth: 400, margin: "0 auto", lineHeight: 1.75 }}>
            No hidden fees. No per-feature charges. No surprises. Just one transparent price for the full platform.
          </p>
        </div>

        {/* ── PRICING CARD ── */}
        <div ref={card.ref} style={{
          ...revealStyle(card.vis),
          background: "#fff", border: "2px solid #e0efcc",
          borderRadius: 24, overflow: "hidden",
          boxShadow: "0 24px 80px rgba(108,187,35,.1), 0 4px 16px rgba(0,0,0,.06)",
          position: "relative",
        }}>

          {/* Best value badge */}
          <div style={{
            position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
            background: G, color: "#fff", borderRadius: "0 0 12px 12px",
            padding: "5px 22px", ...T.zb, fontSize: 11, fontWeight: 700,
            letterSpacing: ".08em", textTransform: "uppercase" as const,
            boxShadow: "0 4px 12px rgba(108,187,35,.35)",
            zIndex: 2,
          }}>
            Best Value · Launch Offer
          </div>

          <div style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
          }}>

            {/* ── LEFT — Price anchor ── */}
            <div style={{
              flex: isDesktop ? "0 0 340px" : "0 0 300px",
              background: "linear-gradient(160deg, #0c1a0c 0%, #172b17 100%)",
              padding: isMobile ? "52px 28px 36px" : "52px 36px 40px",
              display: "flex", flexDirection: "column", justifyContent: "space-between",
              position: "relative", overflow: "hidden",
            }}>

              {/* Ghost Z */}
              <div style={{
                position: "absolute", bottom: -40, right: -20,
                ...T.zh, fontSize: 200, fontWeight: 900, fontStyle: "italic",
                color: "#fff", opacity: .03, lineHeight: 1,
                userSelect: "none", pointerEvents: "none",
              }}>Z</div>

              <div>
                {/* Plan name */}
                <div style={{ marginBottom: 28 }}>
                  <div style={{ ...T.zb, fontSize: 11, fontWeight: 700, color: G, letterSpacing: ".12em", textTransform: "uppercase" as const, marginBottom: 6 }}>
                    Zafflo Pro
                  </div>
                  <div style={{ ...T.zh, fontSize: 17, fontWeight: 800, fontStyle: "italic", color: "rgba(255,255,255,.7)", lineHeight: 1.3 }}>
                    For restaurants, cafés,<br/>hotels, salons & spas
                  </div>
                </div>

                {/* Price */}
                <div style={{ marginBottom: 28 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 6 }}>
                    <span style={{ ...T.zb, fontSize: 16, fontWeight: 600, color: "rgba(255,255,255,.35)", textDecoration: "line-through", marginTop: 8 }}>₹1,099</span>
                    <div>
                      <span style={{ ...T.zh, fontSize: "clamp(44px,5vw,60px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>₹399</span>
                      <span style={{ ...T.zb, fontSize: 13, color: "rgba(255,255,255,.45)", marginLeft: 4 }}>/mo</span>
                    </div>
                  </div>
                  <div style={{ ...T.zb, fontSize: 12, color: "rgba(255,255,255,.35)" }}>
                    Billed annually · ₹4,788/yr + GST
                  </div>
                  {/* Savings pill */}
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: "rgba(108,187,35,.18)", border: "1px solid rgba(108,187,35,.3)",
                    borderRadius: 999, padding: "4px 12px", marginTop: 10,
                    ...T.zb, fontSize: 11, fontWeight: 700, color: G,
                  }}>
                    💰 Save ₹8,400/year
                  </div>
                </div>

                {/* Included bullets */}
                <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 32 }}>
                  {included.map(item => (
                    <div key={item} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Check color={G} />
                      <span style={{ ...T.zb, fontSize: 13, color: "rgba(255,255,255,.65)" }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  className="pc-btn-primary"
                  onClick={() => navigate("/signup")}
                  style={{
                    width: "100%", padding: "16px 24px", borderRadius: 12,
                    ...T.zb, fontSize: 15, fontWeight: 700,
                  }}
                >
                  Start Free Trial →
                </button>
                <button
                  className="pc-btn-outline"
                  onClick={() => document.getElementById("BookDemo")?.scrollIntoView({ behavior: "smooth" })}
                  style={{
                    width: "100%", padding: "13px 24px", borderRadius: 12,
                    border: "1.5px solid rgba(255,255,255,.18)",
                    ...T.zb, fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,.6)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  }}
                >
                  <FaWhatsapp style={{ color: G, fontSize: 14 }} />
                  Book a Demo
                </button>
                <p style={{ ...T.zb, fontSize: 11, color: "rgba(255,255,255,.25)", textAlign: "center", marginTop: 2 }}>
                  * Pricing exclusive of GST
                </p>
              </div>
            </div>

            {/* ── RIGHT — Feature tabs ── */}
            <div style={{ flex: 1, minWidth: 0, padding: isMobile ? "28px 20px 32px" : "36px 36px 40px" }}>

              {/* Section title */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ ...T.zb, fontSize: 11, fontWeight: 700, color: "#bbb", letterSpacing: ".1em", textTransform: "uppercase" as const, marginBottom: 6 }}>
                  What's Included
                </div>
                <div style={{ ...T.zh, fontSize: 20, fontWeight: 900, fontStyle: "italic", color: "#111" }}>
                  Built for your business type
                </div>
              </div>

              {/* Tab row */}
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 7,
                marginBottom: 28, paddingBottom: 24,
                borderBottom: "1.5px solid #f0f0ec",
              }}>
                {featureTabs.map(tab => {
                  const active = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      className="pc-tab"
                      onClick={() => handleTab(tab.key)}
                      style={{
                        ...T.zb,
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "7px 14px", borderRadius: 10,
                        fontSize: 12, fontWeight: 600,
                        background: active ? "#f0f9e6" : "#f5f5f1",
                        border: `1.5px solid ${active ? "#c8e89c" : "transparent"}`,
                        color: active ? "#3b6d11" : "#888",
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{tab.emoji}</span>
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Feature list */}
              <div key={featureKey} style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {activeFeatures.map((feat, i) => (
                  <div
                    key={feat}
                    className="pc-feature-item pc-feature-enter"
                    style={{
                      animationDelay: `${i * 55}ms`,
                      display: "flex", alignItems: "flex-start", gap: 11,
                      padding: "12px 14px", borderRadius: 10,
                      background: i % 2 === 0 ? "#fafaf8" : "transparent",
                      marginBottom: 2,
                    }}
                  >
                    <Check />
                    <span style={{ ...T.zb, fontSize: 13.5, color: "#444", lineHeight: 1.6, fontWeight: 400 }}>
                      {feat}
                    </span>
                  </div>
                ))}
              </div>

              {/* Bottom trust row */}
              <div style={{
                marginTop: 28, paddingTop: 24, borderTop: "1.5px solid #f0f0ec",
                display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
              }}>
                <span style={{ ...T.zb, fontSize: 11, color: "#bbb", marginRight: 4 }}>Also includes:</span>
                {["All 5 verticals", "GST reports", "WhatsApp alerts", "Analytics dashboard", "12+ languages"].map(tag => (
                  <span key={tag} style={{
                    ...T.zb, fontSize: 11, fontWeight: 600,
                    background: "#f0f9e6", color: "#3b6d11",
                    border: "1px solid #c8e89c", borderRadius: 999,
                    padding: "3px 10px",
                  }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

       
      </div>
    </section>
  );
};

export default PricingCTA;